import { unwrapResult } from '@reduxjs/toolkit';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import {
  CommonCustomerPaymentPayload,
  CustomerPaymentData,
  CustomerPaymentListResponse
} from 'src/redux/slices/customer-payment';
import { useSnackBar } from '../use-snackbar';
import Api from 'src/services/ApiService';
import { CommonAxiosResponse } from 'src/types';

const {
  actions: CustomerPaymentActions,
  selectors: CustomerPaymentSelectors
} = slices.customerPayment;

const useCustomerPayment = () => {
  const dispatch = useAppDispatch();
  const snackBar = useSnackBar();

  const customerPaymentParams = useAppSelector(
    CustomerPaymentSelectors?.selectCustomerPaymentParams
  );

  const customerPaymentComputation = useAppSelector(
    CustomerPaymentSelectors?.selectCustomerPaymentComputation
  );

  const customerPaymentList = useAppSelector(
    CustomerPaymentSelectors?.selectCustomerPaymentList
  );

  const fetchComputation = async (params: CommonCustomerPaymentPayload) => {
    try {
      const response = unwrapResult(
        await dispatch(
          CustomerPaymentActions?.customerPaymentComputationThunk(params)
        )
      );

      if (response?.errors) {
        const firstKey = Object.keys(response.errors)[0];
        const firstErrorMessage = response.errors[firstKey][0];
        snackBar.show({
          severity: 'error',
          message: firstErrorMessage || response?.message
        });
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchList = async (params: CommonCustomerPaymentPayload) => {
    try {
      const response = unwrapResult(
        await dispatch(CustomerPaymentActions?.customerPaymentListThunk(params))
      );

      if (response?.errors) {
        const firstKey = Object.keys(response.errors)[0];
        const firstErrorMessage = response.errors[firstKey][0];
        snackBar.show({
          severity: 'error',
          message: firstErrorMessage || response?.message
        });
      }
    } catch (e) {
      console.error(e);
    }
  };

  const setParams = (params: CommonCustomerPaymentPayload) => {
    dispatch(CustomerPaymentActions?.paramsAction(params));
  };

  const exportFetchListData = async (
    params: CommonCustomerPaymentPayload
  ): Promise<CustomerPaymentData[]> => {
    try {
      let exportData: CustomerPaymentData[] = [];

      // Get page 1
      const firstResponse: CommonAxiosResponse<CustomerPaymentListResponse> = await Api.post(
        '/customer/payment/get',
        {
          ...params,
          page: 1,
          action: 'list',
          limit: 300
        }
      );

      if (!firstResponse?.success) return [];

      exportData.push(...(firstResponse?.originalData?.data || []));

      const totalPages = firstResponse?.originalData?.meta?.last_page || 1;
      // Fetch the rest if more than 1 page
      for (let page = 2; page <= totalPages; page++) {
        const pageResponse: CommonAxiosResponse<CustomerPaymentListResponse> = await Api.post(
          '/customer/payment/get',
          {
            ...params,
            page,
            action: 'list',
            limit: 300
          }
        );

        exportData.push(...(pageResponse?.originalData?.data || []));
      }

      return exportData;
    } catch (e) {
      console.error(e);
      return [];
    }
  };

  return {
    customerPaymentParams,
    customerPaymentComputation,
    customerPaymentList,

    fetchComputation,
    fetchList,
    setParams,
    exportFetchListData
  };
};

export default useCustomerPayment;
